/**
 * AdminSachbearbeiterStats — Iter88t (2026-02-19)
 *
 * Zeigt pro Sachbearbeiter (Innendienst) die Anzahl versendeter WP- und
 * PV-Angebote in einem gewählten Monat. Grundlage sind die Snapshot-Felder
 * `sachbearbeiter_name` / `sachbearbeiter_email` in `offer_email_sends`, die
 * beim Versand aus dem Kundendatensatz eingefroren werden.
 *
 * Backend: GET /api/admin/sachbearbeiter-stats?year=&month=
 * Rollen: admin, sales_director, sales_manager
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Calendar, Users, Mail, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

export default function AdminSachbearbeiterStats() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/admin/sachbearbeiter-stats?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Statistik nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const maxCount = useMemo(
    () => Math.max(1, ...(data?.rows || []).map((r) => r.count_total)),
    [data],
  );

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y, y - 1, y - 2];
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <AppHeader />

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 ring-1 ring-amber-500/40 flex items-center justify-center">
            <BarChart3 size={20} className="text-amber-300" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold tracking-tight">Sachbearbeiter-Statistik</h1>
            <p className="text-[12px] text-zinc-400 mt-0.5">
              Versendete Angebote pro Innendienst-Ansprechpartner (aggregiert aus dem Angebots-Versand-Log).
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="rounded-xl bg-[#101015] ring-1 ring-zinc-800 p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">Monat</label>
            <select
              data-testid="sachb-stats-month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-10 rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[13px] focus:border-amber-400 outline-none"
            >
              {MONTHS.map((n, i) => <option key={i + 1} value={i + 1}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5">Jahr</label>
            <select
              data-testid="sachb-stats-year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-10 rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[13px] focus:border-amber-400 outline-none"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-4 text-[12px] text-zinc-400">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={13} />
              {MONTHS[month - 1]} {year}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users size={13} />
              <b className="text-zinc-100">{data?.unique_sachbearbeiter ?? 0}</b> Sachbearbeiter
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Mail size={13} />
              <b className="text-zinc-100">{data?.total_sends ?? 0}</b> Angebote
            </span>
          </div>
        </div>

        {/* Tabelle */}
        <div className="rounded-xl bg-[#101015] ring-1 ring-zinc-800 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-[13px] inline-flex items-center gap-2 justify-center w-full">
              <Loader2 size={14} className="animate-spin" /> Lade …
            </div>
          ) : (data?.rows?.length || 0) === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-[13px]">
              Keine Angebote in {MONTHS[month - 1]} {year} versendet.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-zinc-400 bg-black/40">
                  <th className="text-left px-4 py-2.5 font-bold">Sachbearbeiter</th>
                  <th className="text-left px-4 py-2.5 font-bold">E-Mail</th>
                  <th className="text-right px-4 py-2.5 font-bold w-24">WP</th>
                  <th className="text-right px-4 py-2.5 font-bold w-24">PV</th>
                  <th className="text-right px-4 py-2.5 font-bold w-24">Gesamt</th>
                  <th className="text-left px-4 py-2.5 font-bold w-56">Verteilung</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).map((r, i) => {
                  const pct = Math.round((r.count_total / maxCount) * 100);
                  const isPlaceholder = r.sachbearbeiter_name === "Ohne Sachbearbeiter";
                  return (
                    <tr
                      key={i}
                      data-testid={`sachb-stats-row-${i}`}
                      className="border-t border-zinc-800/60 hover:bg-zinc-900/30 transition"
                    >
                      <td className={`px-4 py-2.5 font-semibold ${isPlaceholder ? "text-zinc-500 italic" : "text-zinc-100"}`}>
                        {r.sachbearbeiter_name}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 text-[12px] font-mono">
                        {r.sachbearbeiter_email || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-zinc-300">{r.count_wp}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-zinc-300">{r.count_pv}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-bold text-amber-300">
                        {r.count_total}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${isPlaceholder ? "bg-zinc-600" : "bg-gradient-to-r from-amber-400 to-amber-500"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-[11px] text-zinc-500 italic">
          Grundlage: <code className="text-zinc-400">offer_email_sends</code>. Der Sachbearbeiter wird
          als Snapshot zum Versandzeitpunkt gespeichert — nachträgliche Änderungen am Kundendatensatz
          verfälschen die Statistik NICHT.
        </p>
      </div>
    </div>
  );
}
